/* eslint-disable no-magic-numbers */
import moment from "moment-timezone";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import MeetingState from "@/constants/MeetingState";

class MeetingPermissions {
	constructor( { userProfile, timezone, meeting, } ) {
		this.userProfile = userProfile;
		this.timezone = timezone;
		this.meeting = meeting;
	}

	//
	// meeting-related methods
	//

	_inviteeCount() {
		const { invitees = [], } = this.meeting;
		return invitees.length;
	}

	_shouldHaveStarted() {
		const now = moment.tz( new Date(), this.timezone );
		const plannedStart = moment.tz( this.meeting.plannedStart * 1000, this.timezone );
		return now.isSameOrAfter( plannedStart );
	}

	isMeetingCreated() {
		const { state, } = this.meeting;
		return state === MeetingState.CREATED;
	}

	isMeetingStarted() {
		const { actualStart, state, } = this.meeting;
		return state === MeetingState.MEETING_STARTED && !!actualStart;
	}

	isMeetingEnded() {
		return [
			MeetingState.MEETING_ENDED,
			MeetingState.MEETING_COMPLETED,
		].includes( this.meeting.state );
	}

	isMeetingFinalized() {
		return this.meeting.state === MeetingState.MEETING_COMPLETED;
	}

	_hasOutcomeReportBeenQueued() {
		const { outcomeReportDelay, } = this.meeting;
		return !( isNull( outcomeReportDelay ) || isUndefined( outcomeReportDelay ) );
	}

	canMeetingHaveTopicRequest() {
		if ( this.meeting.state !== MeetingState.CREATED ) {
			return false;
		}
		if ( this.meetingHasTopicRequest() ) {
			return false;
		}
		return this._inviteeCount() >= 2;
	}

	meetingHasTopicRequest() {
		return this.meeting.requestSent;
	}

	canMeetingBeFinalized() {
		return this.meeting.state === MeetingState.MEETING_ENDED &&
			!this._hasOutcomeReportBeenQueued();
	}

	hasMeetingBeenFinalized() {
		return this.meeting.state === MeetingState.MEETING_COMPLETED ||
			( this.isMeetingEnded() && this._hasOutcomeReportBeenQueued() );
	}

	canMeetingBeStarted() {
		return this.isMeetingCreated();
	}

	//
	// user-related methods
	//

	isUserOrganizer() {
		const { organizer, } = this.meeting;
		return !!( organizer && ( organizer.personId === this.userProfile.personId ) );
	}

	isUserInvitee() {
		const { invitees = [], } = this.meeting;
		return !!invitees.find( invitee => invitee.personId === this.userProfile.personId );
	}

	isUserTeamMember() {
		const { teams, } = this.meeting;
		return !!teams.find( team => team.isMember );
	}

	//
	// meeting topics
	//

	canUserRequestTopics() {
		return this.isUserOrganizer();
	}

	canUserAddTopics() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserEditTopics() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserDeferTopics() {
		return this.meeting.isRecurring && ( this.isUserOrganizer() || this.isUserInvitee() );
	}

	canUserUndeferTopics() {
		return this.meeting.isRecurring && ( this.isUserOrganizer() || this.isUserInvitee() );
	}

	canUserRemoveTopics() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserActivateTopics() {
		// TODO: implementation
		return true;
	}

	//
	// meeting team(s)
	//

	canAssignTeam() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	//
	// meeting goals
	//

	canViewGoals() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canManageGoals() {
		return this.isUserTeamMember();
	}

	canChangeGoalConfidence() {
		return this.isUserTeamMember();
	}

	canChangeKeyResultProgress() {
		return this.isUserTeamMember();
	}

	canChangeKeyResultConfidence() {
		return this.isUserTeamMember();
	}

	//
	// meeting commitments
	//

	canViewCommitments() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canCompleteCommitments() {
		return this.isUserOrganizer() || this.isUserTeamMember();
	}

	//
	// past meetings reports
	//

	canViewPastMeetingReports() {
		const { meeting, } = this;
		return ( this.isUserOrganizer() || this.isUserInvitee() ) && !!meeting.isRecurring;
	}

	//
	// finalization
	//

	canUserFinalizeMeeting() {
		return this.isUserOrganizer();
	}

	canUserStartMeeting() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserJoinMeeting() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserLeaveMeeting() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserTakePrivateNotes() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserChangeActiveTopic() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserManageOutcomes() {
		return this.isUserOrganizer() || this.isUserInvitee();
	}

	canUserViewOutcomes() {
		return this.isUserOrganizer() ||
			this.isUserInvitee();
	}

	resolve() {
		return {
			meetingId: this.meeting.id,
			userId: this.userProfile.personId,
			// status
			canMeetingBeFinalized: this.canMeetingBeFinalized(),
			canMeetingBeStarted: this.canMeetingBeStarted(),
			canMeetingHaveTopicRequest: this.canMeetingHaveTopicRequest(),
			isMeetingCreated: this.isMeetingCreated(),
			isMeetingEnded: this.isMeetingEnded(),
			isMeetingFinalized: this.isMeetingFinalized(),
			isMeetingStarted: this.isMeetingStarted(),
			meetingHasTopicRequest: this.meetingHasTopicRequest(),
			hasMeetingBeenFinalized: this.hasMeetingBeenFinalized(),
			// permissions
			canAssignTeam: this.canAssignTeam(),
			canChangeGoalConfidence: this.canChangeGoalConfidence(),
			canChangeKeyResultConfidence: this.canChangeKeyResultConfidence(),
			canChangeKeyResultProgress: this.canChangeKeyResultProgress(),
			canCompleteCommitments: this.canCompleteCommitments(),
			canManageGoals: this.canManageGoals(),
			canUserActivateTopics: this.canUserActivateTopics(),
			canUserAddTopics: this.canUserAddTopics(),
			canUserChangeActiveTopic: this.canUserChangeActiveTopic(),
			canUserDeferTopics: this.canUserDeferTopics(),
			canUserEditTopics: this.canUserEditTopics(),
			canUserFinalizeMeeting: this.canUserFinalizeMeeting(),
			canUserJoinMeeting: this.canUserJoinMeeting(),
			canUserLeaveMeeting: this.canUserLeaveMeeting(),
			canUserManageOutcomes: this.canUserManageOutcomes(),
			canUserRemoveTopics: this.canUserRemoveTopics(),
			canUserRequestTopics: this.canUserRequestTopics(),
			canUserStartMeeting: this.canUserStartMeeting(),
			canUserTakePrivateNotes: this.canUserTakePrivateNotes(),
			canUserUndeferTopics: this.canUserUndeferTopics(),
			canUserViewOutcomes: this.canUserViewOutcomes(),
			canViewCommitments: this.canViewCommitments(),
			canViewGoals: this.canViewGoals(),
			canViewPastMeetingReports: this.canViewPastMeetingReports(),
			isUserInvitee: this.isUserInvitee(),
			isUserOrganizer: this.isUserOrganizer(),
		};
	}
}

export default MeetingPermissions;



// WEBPACK FOOTER //
// ./src/permissions/MeetingPermissions.js